<script setup lang="ts">
import {DashboardWidgetProps, dashboardPresenterKey, reportIsDashboardTypeKey, tableWidgetGridApiKey} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import {GridReadyEvent, StateUpdatedEvent} from "ag-grid-enterprise";
import {AgGridVue} from "ag-grid-vue3";
import {ColDef, GridState, ICellRendererParams} from "ag-grid-community";
import {computed, inject, nextTick, ref, shallowRef, toValue, watch} from "vue";
import DashboardConstants from "@/utils/constants/DashboardConstants.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import StorageUtils from "@/utils/common/StorageUtils.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import RefreshIcon from "@/assets/editViewIcons/RefreshIcon.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import ExportExcelIcon from "@/assets/listViewIcons/ExportExcelIcon.vue";
import dayjs from "@/utils/dayjs";
import AGGridColumnFactory from "@/utils/UIConstants/AGGridColumns/AGGridColumnFactory.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import WizardSlotSelectorButton from "@/components/bi/WizardSlotSelectorButton.vue";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import {useDashboardViewOptionsStore} from "@/stores/dashboardViewOptionsStore.ts";
import {useBIInteractionsForAgGrid} from "@/components/dashboardWidgets/useBIInteractionsForAgGrid.ts";
import type {
  BIDrillDownClick,
  BIDrillDownByClick,
  CrossFilterEmission,
} from "@/components/bi/biChartTypes.ts";
import DTODashBoardResultSet = namaTypes.DTODashBoardResultSet;
import DTODashboardValue = namaTypes.DTODashboardValue;
import BIWidgetRuntimeSelectorInfo = namaTypes.BIWidgetRuntimeSelectorInfo;

const props = defineProps<DashboardWidgetProps>();
const emit = defineEmits<{
  crossFilterClicked: [emissions: CrossFilterEmission[]];
  drillDownRequested: [click: BIDrillDownClick];
  drillDownByRequested: [click: BIDrillDownByClick];
}>();
const chartLine = ref(props.chartLine);
const dashboardPresenter = inject(dashboardPresenterKey, undefined);
const gridApi = inject(tableWidgetGridApiKey, shallowRef())
const isRefreshClicked = ref(false);
const columnDefs = ref<ColDef[]>([]);
const initialState = ref<GridState | undefined>(undefined);
const isGridReady = ref(false);
const headerNames = ref<string[]>([]);
const rowData = ref<any[] | undefined>(undefined);
const parameters = computed(() => chartLine.value.parameters);
const resultSet = computed<DTODashBoardResultSet>(() => chartLine.value.resultSet!);
const runtimeSelectorInfo = computed<BIWidgetRuntimeSelectorInfo | undefined>(
  () => (chartLine.value as any)?.runtimeSelectorInfo);
const defaultColDef: ColDef = {
  filter: true,
  filterParams: {
    cellRenderer: isInHTML() ? (params: ICellRendererParams) => params.value : undefined,
    applyMiniFilterWhileTyping: true,
    textFormatter: (value: string): string | null => value == null ? null : StringUtils.replaceArNumerals(value),
    buttons: ["reset"],
  },
  wrapText: true,
  autoHeight: true,
  flex: 1,
  minWidth: 130,
  enableValue: true,
  enableRowGroup: true,
  enablePivot: true,
  cellClass: (params) => cellClassForInteraction(params) ?? '',
};
const pivotPanelShow = ref<"always" | "onlyWhenPivoting" | "never">(
  "always",
);

const reportIsDashboardType = inject(reportIsDashboardTypeKey, computed(() => false))

async function handleInitialGridState() {
  const storedState = NamaCommon.getStoredValue(StorageUtils.calcDashboardTableStatePath(chartLine.value), undefined)
  isGridReady.value = false;
  await nextTick();
  initialState.value = storedState
  removeDefaultColDefWidthIfNeeded();
  isGridReady.value = true;
}

function removeDefaultColDefWidthIfNeeded() {
  if (!initialState.value)
    return;
  const colDefs = toValue(columnDefs) ?? []
  const columnSizing = initialState.value.columnSizing?.columnSizingModel ?? []
  for (const column of columnSizing) {
    const initialWidth = column.width;
    const colId = column.colId;
    let find = AGGridUtils.findColDef(colDefs, colId);
    if (!find)
      continue;
    const isChanged = find.width !== initialWidth;
    if (isChanged)
      find.width = undefined;
  }
}

watch(() => props.chartLine, async (newLine) => {
  chartLine.value = newLine;
  isGridReady.value = false;
  await drawTable();
  isGridReady.value = true;
});

async function drawTable() {
  columnDefs.value = [];
  headerNames.value = [];
  rowData.value = undefined;
  calcTableHeaderNames();
  appendColDefs();
  appendRowData();
  await handleInitialGridState();
}

function applyPivotModeSettings() {
  const gridApiValue = gridApi.value;
  if (!gridApiValue)
    return;
  gridApiValue.setGridOption("pivotMode", openPivotModeByDefault());
  const pivotRowGroups: string = parameters.value?.[DashboardConstants.Param_PivotRowGroupColumns];
  const pivotValues: string = parameters.value?.[DashboardConstants.Param_PivotValueColumns];
  const pivotColumnNames: string = parameters.value?.[DashboardConstants.Param_PivotColumns];
  if (ObjectChecker.isNotEmptyOrNull(pivotRowGroups))
    gridApiValue.setRowGroupColumns(pivotRowGroups.split(","));
  if (ObjectChecker.isNotEmptyOrNull(pivotValues))
    gridApiValue.setValueColumns(pivotValues.split(","));
  if (ObjectChecker.isNotEmptyOrNull(pivotColumnNames))
    gridApiValue.setPivotColumns(pivotColumnNames.split(","));
}

function isInHTML() {
  return parameters.value && parameters.value[DashboardConstants.Param_INHTML] === "true";
}

function openPivotModeByDefault() {
  return parameters.value && parameters.value[DashboardConstants.Param_PivotOpenByDefault] == "true";
}

function calcTableHeaderNames() {
  if (!parameters.value || (!parameters.value[DashboardConstants.Param_TableArabicHeader] && !parameters.value[DashboardConstants.Param_TableEnglishHeader]))
    return;
  const arHeaders = StringUtils.csvLineToList((parameters.value[DashboardConstants.Param_TableArabicHeader] ?? ""));
  const enHeaders = StringUtils.csvLineToList((parameters.value[DashboardConstants.Param_TableEnglishHeader] ?? ""));
  const maxLength = Math.max(arHeaders.length, enHeaders.length);
  for (let i = 0; i < maxLength; i++) {
    const arHeader = arHeaders.length - 1 >= i ? arHeaders[i] : undefined;
    const enHeader = enHeaders.length - 1 >= i ? enHeaders[i] : undefined;
    headerNames.value.push(Translator.getName(arHeader, enHeader));
  }
}

function appendColDefs() {
  if (!resultSet.value.rows || !resultSet.value.rows.length || !resultSet.value.rows[0] || !resultSet.value.headerNames || !resultSet.value.headerNames.length)
    return;
  for (let i = 0; i < (resultSet.value.rows![0].values ?? []).length; i++) {
    const columnKey = resultSet.value.headerNames[i];
    const cellDataType = resultSet.value.agColumnCellDataTypes?.[columnKey];
    const colDef = AGGridColumnFactory.createColDefForCellDataType(columnKey, cellDataType);
    colDef.headerName = i <= headerNames.value.length - 1 ? headerNames.value[i] : StringUtils.camelCaseToSeparatedWords(columnKey);
    if (isInHTML())
      colDef.cellRenderer = (params: ICellRendererParams) => params.value;
    columnDefs.value.push(colDef);
  }
}

function appendRowData() {
  rowData.value = [];
  if (!resultSet.value.rows || !resultSet.value.rows.length || !resultSet.value.headerNames || !resultSet.value.headerNames.length)
    return;
  for (let j = 1; j < resultSet.value.rows!.length; j++) {
    let row = resultSet.value.rows![j];
    const object: Record<string, any> = {};
    for (let i = 0; i < resultSet.value.headerNames!.length; i++) {
      let headerName = resultSet.value.headerNames![i];
      let value: DTODashboardValue = {};
      if (row.values)
        value = row.values[i] ?? {};
      object[headerName] = ObjectChecker.getFirstNotNullObj(value.d, value.b, ObjectChecker.toStringOrEmpty(value.s));
    }
    // Server-order index (row 0 is the header row). Interaction points are indexed by this, not by
    // AG Grid's display rowIndex, which changes after sorting — see useBIInteractionsForAgGrid.
    object._biRowIdx = j - 1;
    rowData.value.push(object);
  }
}

watch([gridApi, chartLine], () => {
  if (!gridApi.value || !chartLine.value)
    return;
  applyPivotModeSettings();
}, {immediate: true})

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
  params.api.setGridOption('getContextMenuItems', getContextMenuItems);
  if (!initialState.value)
    gridApi.value.autoSizeAllColumns(false);
}

const onStateUpdated = (event: StateUpdatedEvent) => {
  NamaCommon.setInStorage(StorageUtils.calcDashboardTableStatePath(chartLine.value), event.state);
};

function onPivotModeChanged() {
  if (!gridApi.value)
    return
  const pivotMode = gridApi.value?.isPivotMode();
  if (pivotMode)
    gridApi.value.setGridOption("grandTotalRow", "top");
  else
    gridApi.value.setGridOption("grandTotalRow", undefined);
}

drawTable();

// --- BI interaction (click-emit, drill-down, links, drill-down-by) ---
const widgetId = computed<string | undefined>(() => chartLine.value.widgetId);
const {
  isFilterSource,
  isFilteredByOther,
  filteredByTooltip,
  emittingTooltip,
  onCellClicked,
  getContextMenuItems,
  cellClassForInteraction,
} = useBIInteractionsForAgGrid({
  chartLine,
  widgetId,
  dashboardId: () => props.dashboardId,
  emit: {
    crossFilterClicked: (emissions) => emit('crossFilterClicked', emissions),
    drillDownRequested: (click) => emit('drillDownRequested', click),
    drillDownByRequested: (click) => emit('drillDownByRequested', click),
  },
});

async function onRefreshClicked() {
  if (isRefreshClicked.value)
    return;
  isRefreshClicked.value = true;
  try {
    if (dashboardPresenter) {
      await dashboardPresenter.redrawWidgets(chartLine.value.widgetId);
      return;
    }
    await legacyManualRefresh();
  } finally {
    isRefreshClicked.value = false;
  }
}

async function legacyManualRefresh() {
  let response = await NamaCommon.namaFetch(NamaURLS.FetchCharts, {
    params: {
      dashBoardId: props.dashboardId,
      widgetId: chartLine.value.widgetId,
    },
    data: [],
    noLoadingIndicator: true
  });
  if (!NamaCommon.isSuccess(response) || !response.data || !response.data.chartLines || !response.data.chartLines.length)
    return;
  let myChartLine = response.data.chartLines.find(l => l.widgetId === chartLine.value.widgetId);
  if (!myChartLine)
    return;
  chartLine.value = myChartLine;
  isGridReady.value = false;
  await drawTable();
  isGridReady.value = true;
}

function onGroupExpand() {
  let isRowExpanded: boolean | null = null;
  const rowNodes = gridApi.value?.getRenderedNodes();
  if (rowNodes) {
    for (const rowNode of rowNodes) {
      if (!rowNode.group)
        continue;
      isRowExpanded = !!rowNode.expanded;
      break;
    }
  }
  if (isRowExpanded)
    gridApi.value?.collapseAll();
  else
    gridApi.value?.expandAll();
}

function onExportToExcel() {
  gridApi.value?.exportDataAsExcel({fileName: (props.chartLine.enChartTitle ?? props.chartLine.arChartTitle ?? "Table.Dashboard") + "." + dayjs().format("yyyyMMDD.HHmm") + ".xlsx"});
}

const viewOptionsStore = useDashboardViewOptionsStore();
const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());
const showAdminToolbox = computed(() => isAdmin.value && viewOptionsStore.showToolbox);

function openWidgetEditView() {
  const id = widgetId.value;
  if (!id) return;
  const link = PlaceUtils.editLink('DashBoardWidget', id);
  if (link) window.open(link, '_blank');
}
</script>

<template>
  <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-rounded tw-relative"
       :class="{'bi-widget-source': isFilterSource, 'bi-widget-filtered': isFilteredByOther}">
    <div v-if="isFilterSource || isFilteredByOther"
         class="tw-absolute tw-top-1 tw-end-1 tw-z-10 tw-flex tw-items-center tw-gap-1">
      <div v-if="isFilterSource"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-primary tw-text-white tw-shadow"
           :title="emittingTooltip">
        <q-icon name="filter_alt" size="14px" class="emit-funnel"/>
      </div>
      <div v-if="isFilteredByOther"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-info tw-text-white tw-shadow"
           :title="filteredByTooltip">
        <q-icon name="filter_alt" size="14px"/>
      </div>
    </div>
    <div class="tw-flex tw-items-center tw-gap-1 tw-my-1 dark:tw-text-white">
      <template v-if="!reportIsDashboardType">
        <a
          class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer"
          :class="{'nama-toolbar-disabled':isRefreshClicked}"
          @click.stop="onRefreshClicked" :title="Translator.translateId(BusinessFunction.refresh,'BusinessFunction')">
          <RefreshIcon class="tw-h-5" :class="{'!tw-fill-gray-300 dark:!tw-fill-gray-600':isRefreshClicked}"/>
        </a>
        <a
          class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer "
          :class="{'nama-toolbar-disabled':isRefreshClicked}"
          @click.stop="onExportToExcel" :title="Translator.translateId(BusinessFunction.exportAsXLSX)">
          <ExportExcelIcon class="tw-h-5" :class="{'!tw-fill-gray-300 dark:!tw-fill-gray-600':isRefreshClicked}"/>
        </a>
      </template>
      <a
        class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer "
        @click.stop="onGroupExpand">
        <NamaIcon default-icon="nm-expand"/>
      </a>
      <a v-if="showAdminToolbox"
         class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer"
         :title="'Open widget edit screen (admin)'"
         @click.stop="openWidgetEditView">
        <q-icon name="open_in_new" size="18px"/>
      </a>
      <WizardSlotSelectorButton v-if="runtimeSelectorInfo && widgetId"
                                :widget-id="widgetId"
                                :info="runtimeSelectorInfo"
                                @applied="async () => { if (widgetId) await dashboardPresenter?.redrawWidgets(widgetId); }"
                                @reset="async () => { if (widgetId) await dashboardPresenter?.redrawWidgets(widgetId); }"/>
      <span class="tw-line-clamp-1 tw-font-bold tw-text-base"
            :title="Translator.getName(props.chartLine.arChartTitle, props.chartLine.enChartTitle)"
            v-text="Translator.getName(props.chartLine.arChartTitle, props.chartLine.enChartTitle)"/>
    </div>
    <AgGridVue ref="gridElement" v-if="isGridReady"
               v-bind="{...AGGridUtils.getTableDashboardSideBarConfig()}"
               style="width: 100%; height: 100%;font-size: var(--ag-font-size);"
               class="listview-grid"
               groupDisplayType="multipleColumns"
               rowModelType="clientSide"
               :pagination="true"
               :columnDefs="columnDefs"
               :rowNumbers="true"
               :paginationPageSize="25"
               :paginationPageSizeSelector="[25, 50, 100, 250, 1000, 2500, 5000]"
               :cellSelection="true"
               :showOpenedGroup="true"
               :suppressHeaderFocus="true"
               :enableRtl="Translator.isLanguageRTL()"
               :localeText="Translator.fetchAGGridLocale()"
               @cell-clicked="onCellClicked"
               @grid-ready="onGridReady"
               :rowData="rowData"
               :pivotPanelShow="pivotPanelShow"
               :defaultColDef="defaultColDef"
               :initialState="initialState"
               @state-updated="onStateUpdated"
               @column-pivot-mode-changed="onPivotModeChanged"
               :enableCellTextSelection="true"
               :processCellForClipboard="AGGridUtils.processCellForClipboard"/>
  </div>
</template>

<style scoped>
.bi-widget-source {
  box-shadow: inset 0 0 0 2px var(--q-primary);
}

.bi-widget-filtered {
  box-shadow: inset 0 0 0 1px var(--q-info);
}

.bi-widget-source.bi-widget-filtered {
  box-shadow: inset 0 0 0 2px var(--q-primary), inset 0 0 0 4px var(--q-info);
}

.emit-funnel {
  transform: rotate(180deg);
}
</style>

<style>
.listview-grid .bi-clickable-cell {
  cursor: pointer;
}

.listview-grid .bi-clickable-cell:hover {
  text-decoration: underline;
}
</style>
